import React, { Suspense, lazy } from "react";
import { HashRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Navbar from "./components/Navbar/Navbar";
import Home from "./pages/Home/Home";
import HomeMobile from "./pages/Home/HomeMobile";
import Footer from "./components/Footer/Footer";
import FooterMobile from "./components/Footer/FooterMobile";
import WorkInProgress from "./pages/WorkInProgress";
import useWindowSize from "./hooks/useWindowSize";
import useReducedMotion from "./hooks/useReducedMotion";
import NavbarMobile from "./components/Navbar/NavbarMobile";
import ScrollToTop from "./components/ScrollToTop";
import ErrorBoundary from "./components/ErrorBoundary";
import './App.css';

/* Lazy-loaded routes — keeps initial bundle lean */
const AboutMe = lazy(() => import("./pages/AboutMe/AboutMe"));
const AboutMeMobile = lazy(() => import("./pages/AboutMe/AboutMeMobile"));
const Energy = lazy(() => import("./pages/Energy/Energy"));
const EnergyMobile = lazy(() => import("./pages/Energy/EnergyMobile"));
const Robotics = lazy(() => import("./pages/Robotics/Robotics"));
const RoboticsMobile = lazy(() => import("./pages/Robotics/RoboticsMobile"));
const Experience = lazy(() => import("./pages/Experience/Experience"));
const ExperienceMobile = lazy(() => import("./pages/Experience/ExperienceMobile"));
const Arctic = lazy(() => import("./pages/Arctic/Arctic"));
const ArcticMobile = lazy(() => import("./pages/Arctic/ArcticMobile"));

const pageTransition = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } },
  exit: { opacity: 0, y: -12, transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] } },
};

const reducedMotionTransition = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

/* Minimal loading fallback for lazy routes */
function RouteFallback() {
  return (
    <div style={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: 0.4,
    }}>
      <div className="route-loading-spinner" />
    </div>
  );
}

function AnimatedRoutes({ isMobile, reducedMotion }) {
  const location = useLocation();
  const variants = reducedMotion ? reducedMotionTransition : pageTransition;
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        style={{ width: "100%" }}
      >
        <Suspense fallback={<RouteFallback />}>
          <Routes location={location}>
            <Route path="/" element={isMobile ? <HomeMobile /> : <Home />} />
            <Route path="/about" element={isMobile ? <AboutMeMobile /> : <AboutMe />} />
            <Route path="/energy" element={isMobile ? <EnergyMobile /> : <Energy />} />
            <Route path="/robotics" element={isMobile ? <RoboticsMobile /> : <Robotics />} />
            <Route path="/experience" element={isMobile ? <ExperienceMobile /> : <Experience />} />
            <Route path="/arctic" element={isMobile ? <ArcticMobile /> : <Arctic />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  const [width] = useWindowSize();
  const reducedMotion = useReducedMotion();
  if (process.env.REACT_APP_WIP_MODE === 'true') {
    return <WorkInProgress />;
  }
  const isMobile = width < 600;
  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        {isMobile ? <NavbarMobile /> : <Navbar />}
        <div className="container">
          <AnimatedRoutes isMobile={isMobile} reducedMotion={reducedMotion} />
        </div>
        {isMobile ? <FooterMobile /> : <Footer />}
      </Router>
    </ErrorBoundary>
  );
}

export default App;
